<template>
  <div class="diagnosis-template-manager space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 页面标题 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5D76E] to-[#F2C94C] flex items-center justify-center shadow-sm">
          <svg class="w-5 h-5 text-[#1B3C59]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-black text-slate-900">诊断模板</h3>
        </div>
      </div>
    </div>

    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">诊断模板决定前台「聊一聊」AI 深度诊断的问题生成和处方输出逻辑</p>
      </div>
    </div>

    <!-- 搜索 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-3">
        <div class="relative flex-1 max-w-md">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索话题..."
            class="w-full px-4 py-1.5 pl-9 bg-slate-50 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-[#F2C94C] focus:ring-2 focus:ring-[#F2C94C]/20 transition-all"
          />
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>
    </div>

    <!-- 模板列表 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-table :data="filteredTemplates" stripe size="small" style="width: 100%">
        <el-table-column prop="topicId" label="话题ID" width="80" />
        <el-table-column label="关联话题" min-width="200">
          <template #default="{ row }">
            <span class="text-sm font-medium text-slate-900">{{ row.topicTitle }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Truth 摘要" min-width="250">
          <template #default="{ row }">
            <span class="text-sm text-slate-600 line-clamp-2">{{ row.truthPreview }}</span>
          </template>
        </el-table-column>
        <el-table-column label="话术" width="80" align="center">
          <template #default="{ row }">
            <span class="text-sm text-slate-500">{{ row.scriptCount }} 段</span>
          </template>
        </el-table-column>
        <el-table-column label="红线" width="80" align="center">
          <template #default="{ row }">
            <span class="inline-flex items-center px-2 py-0.5 bg-red-50 text-red-500 text-xs rounded font-medium">{{ row.redLinesCount }} 条</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <button v-if="hasPerm(PERMISSIONS.EDIT_DIAGNOSIS)" @click="handleEdit(row)" class="text-[#F2C94C] hover:text-[#E5B73B] text-sm font-medium transition-colors">编辑</button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="filteredTemplates.length === 0" class="text-center py-12">
        <svg class="w-12 h-12 text-slate-200 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <div class="text-slate-400 text-sm">暂无模板</div>
      </div>
    </div>

    <!-- 编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="800px"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <el-tabs v-model="activeTab">
        <!-- Truth -->
        <el-tab-pane label="诊断真相" name="truth">
          <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 mt-2 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
            <div class="flex items-center gap-2 mb-4">
              <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h4 class="text-sm font-black text-slate-900">诊断真相（Truth）</h4>
            </div>
            <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>
            <el-input
              v-model="form.truth"
              type="textarea"
              :rows="10"
              placeholder="请输入诊断真相内容（支持 Markdown）..."
            />
          </div>
        </el-tab-pane>

        <!-- Script -->
        <el-tab-pane label="沟通话术" name="script">
          <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 mt-2 space-y-4 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
            <div class="flex items-center gap-2 mb-4">
              <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <h4 class="text-sm font-black text-slate-900">沟通话术（Script）</h4>
            </div>
            <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

            <el-form :model="form.script" label-width="80px" size="small">
              <el-form-item label="开场白">
                <el-input v-model="form.script.opening" placeholder="对话开场白..." />
              </el-form-item>
              <el-form-item label="回应">
                <div v-for="(resp, idx) in form.script.responses" :key="idx" class="flex items-center gap-2 mb-2">
                  <el-input v-model="form.script.responses[idx]" placeholder="回应内容..." />
                  <button
                    v-if="form.script.responses.length > 1"
                    @click="form.script.responses.splice(idx, 1)"
                    type="button"
                    class="text-xs text-red-400 hover:text-red-500 flex-shrink-0"
                  >
                    删除
                  </button>
                </div>
                <button
                  @click="form.script.responses.push('')"
                  type="button"
                  class="text-xs text-[#F2C94C] hover:text-[#E5B73B] font-medium"
                >
                  + 添加回应
                </button>
              </el-form-item>
              <el-form-item label="结束语">
                <el-input v-model="form.script.closing" placeholder="对话结束语..." />
              </el-form-item>
            </el-form>
          </div>
        </el-tab-pane>

        <!-- RedLines -->
        <el-tab-pane label="红线规则" name="redLines">
          <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 mt-2 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
            <div class="flex items-center gap-2 mb-4">
              <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h4 class="text-sm font-black text-slate-900">红线规则（RedLines）</h4>
            </div>
            <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

            <div v-for="(line, idx) in form.redLines" :key="idx" class="flex items-center gap-2 mb-2">
              <span class="inline-flex items-center px-2 py-0.5 bg-red-50 text-red-500 text-xs rounded font-medium flex-shrink-0">红线 {{ idx + 1 }}</span>
              <el-input v-model="form.redLines[idx]" size="small" placeholder="红线内容..." />
              <button
                v-if="form.redLines.length > 1"
                @click="form.redLines.splice(idx, 1)"
                type="button"
                class="text-xs text-red-400 hover:text-red-500 flex-shrink-0"
              >
                删除
              </button>
            </div>
            <button
              @click="form.redLines.push('')"
              type="button"
              class="text-xs text-[#F2C94C] hover:text-[#E5B73B] font-medium mt-2"
            >
              + 添加红线
            </button>
          </div>
        </el-tab-pane>
      </el-tabs>

      <span slot="footer">
        <button
          @click="dialogVisible = false"
          class="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors mr-2"
        >
          取消
        </button>
        <button
          v-if="hasPerm(PERMISSIONS.EDIT_DIAGNOSIS)"
          @click="handleSave"
          class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
        >
          保存
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { logAction } from '../../services/audit'
import { PRESCRIPTION_DATA, TOPICS } from '../../data'
import { PERMISSIONS } from '../../data/adminConfig'

const DEFAULT_FORM = {
  topicId: '',
  truth: '',
  script: {
    opening: '',
    responses: [''],
    closing: '',
  },
  redLines: [''],
}

export default {
  name: 'DiagnosisTemplateManager',

  data() {
    return {
      dialogVisible: false,
      dialogTitle: '编辑诊断模板',
      activeTab: 'truth',
      form: JSON.parse(JSON.stringify(DEFAULT_FORM)),
      searchQuery: '',
    }
  },

  computed: {
    ...mapState(['prescriptions']),
    ...mapGetters(['hasPermission']),

    PERMISSIONS() {
      return PERMISSIONS
    },

    templateList() {
      const dict = this.prescriptions || PRESCRIPTION_DATA
      return Object.entries(dict).map(([topicId, value]) => {
        const topic = TOPICS.find(t => t.id === topicId)
        return {
          topicId,
          topicTitle: topic?.title || `话题 ${topicId}`,
          truthPreview: value.truth?.slice(0, 100) + '...',
          truth: value.truth || '',
          script: value.script || { opening: '', responses: [''], closing: '' },
          redLines: value.redLines || [],
          scriptCount: (value.script?.responses?.length || 0) + 2,
          redLinesCount: value.redLines?.length || 0,
        }
      })
    },

    filteredTemplates() {
      let list = [...this.templateList]
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        list = list.filter(t =>
          t.topicTitle.toLowerCase().includes(q) ||
          t.truth.toLowerCase().includes(q)
        )
      }
      return list
    },
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    handleEdit(row) {
      this.dialogTitle = `编辑诊断模板 - ${row.topicTitle}`
      this.form = {
        topicId: row.topicId,
        truth: row.truth,
        script: {
          opening: row.script?.opening || '',
          responses: [...(row.script?.responses || [''])],
          closing: row.script?.closing || '',
        },
        redLines: [...(row.redLines || [''])],
      }
      this.activeTab = 'truth'
      this.dialogVisible = true
    },

    handleSave() {
      if (!this.hasPerm(PERMISSIONS.EDIT_DIAGNOSIS)) {
        this.$message.error('您没有编辑诊断模板的权限')
        return
      }

      const dict = { ...(this.prescriptions || PRESCRIPTION_DATA) }
      dict[this.form.topicId] = {
        truth: this.form.truth,
        script: {
          opening: this.form.script.opening,
          responses: this.form.script.responses.filter(r => r.trim()),
          closing: this.form.script.closing,
        },
        redLines: this.form.redLines.filter(l => l.trim()),
      }

      this.$store.commit('SET_PRESCRIPTIONS', dict)

      logAction({
        action: 'UPDATE',
        targetType: 'prescription',
        targetId: this.form.topicId,
        reason: '编辑诊断模板',
        source: 'admin-ui',
        actor: { role: this.$store.state.userRole },
      })

      this.$message.success('保存成功')
      this.dialogVisible = false
    },

    resetForm() {
      this.form = JSON.parse(JSON.stringify(DEFAULT_FORM))
    },
  },
}
</script>
